import type { UpdateTransmissionProgressionBodyIds } from '@toktokdoctech/tokapi-client'
import { toPJAnswerValue } from 'apps/ids/src/app/components/utils/file'
import { CURRENT_QUESTION_KEY, SKIPPED_QUESTIONS_KEY } from '../constants'

/**
 * Converts the map of committed responses (question -> [values]) into the payload
 * expected by the transmission progress API.
 *
 * - Flattens the responses (a single value if the array contains only one element).
 * - Ignores unanswered questions.
 * - Skipped questions (committed with an empty answer) are listed under
 *   the special key `__skippedQuestionIds__`, so the path can be rebuilt on reload.
 * - Optionally adds the user’s current position (`positionId`)
 *   under the special key `__currentQuestionId__`, used to restore
 *   their progress upon reloading.
 */
export const buildTransmissionPayload = (
    latestCommittedMap: Map<string, any[]>,
    positionId?: string | null,
): UpdateTransmissionProgressionBodyIds => {
    const requestMap = new Map<string, any>()
    const skippedQuestionIds: string[] = []

    latestCommittedMap.forEach((answers, questionId) => {
        if (questionId.startsWith('__')) return
        if (!answers || answers.length === 0) {
            skippedQuestionIds.push(questionId)
            return
        }
        const values = answers.map(toPJAnswerValue)
        const formattedValue = values.length === 1 ? values[0] : values
        requestMap.set(questionId, formattedValue)
    })
    if (skippedQuestionIds.length > 0) {
        requestMap.set(SKIPPED_QUESTIONS_KEY, skippedQuestionIds)
    }
    if (positionId) {
        requestMap.set(CURRENT_QUESTION_KEY, positionId)
    }
    const request: any[] = []
    requestMap.forEach((value, id) => {
        request.push({ id, value })
    })
    return { request }
}
